import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Calendar, Clock, Package, Plus } from 'lucide-react';
import { mockLocations } from '../mock';
import SavedPlacesQuickPicks from '../components/SavedPlacesQuickPicks';
import {
  LocationInput,
  StopManager,
  ScheduleRideForm,
  FeaturesSection,
  LogisticsPromo,
  BusBookingPromo,
  CTASection
} from './home';

const Home = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('ride');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [featureFlags, setFeatureFlags] = useState({ bus_booking_visible: false, logistics_visible: false });
  const [stops, setStops] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showStopSuggestions, setShowStopSuggestions] = useState(-1);
  const [savedAddresses, setSavedAddresses] = useState({ home: '', work: '', favorites: [] });
  const [user, setUser] = useState(null);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('tekstar_token');
      if (!token) return;

      try {
        const api = (await import('../utils/api')).default;
        const response = await api.get('/auth/me');
        setUser(response.data.user);
        if (response.data.user.savedAddresses) {
          setSavedAddresses(response.data.user.savedAddresses);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const api = (await import('../utils/api')).default;
        const res = await api.get('/feature-flags');
        setFeatureFlags(res.data);
      } catch (e) {}
    };
    fetchFlags();
  }, []);

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const api = (await import('../utils/api')).default;
          const response = await api.get(`/maps/geocode/reverse?lat=${latitude}&lng=${longitude}`);
          
          if (response.data.success && response.data.location?.address) {
            setPickup(response.data.location.address);
          } else {
            setPickup(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          }
        } catch (error) {
          console.error('Error getting address:', error);
          const { latitude, longitude } = position.coords;
          setPickup(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            alert('Location permission denied. Please enable location access in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information is unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out. Please try again.');
            break;
          default:
            alert('An error occurred while getting your location.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const addStop = () => {
    if (stops.length >= 2) {
      alert('Maximum 2 stops allowed');
      return;
    }
    setStops([...stops, { location: '', duration: 5 }]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const updateStop = (index, field, value) => {
    const newStops = [...stops];
    newStops[index][field] = value;
    setStops(newStops);
  };

  const handleGetRide = () => {
    if (!pickup || !dropoff) {
      alert('Please enter both pickup and dropoff locations');
      return;
    }

    if (stops.length > 0) {
      const emptyStops = stops.filter(stop => !stop.location.trim());
      if (emptyStops.length > 0) {
        alert('Please fill in all stop locations or remove empty stops');
        return;
      }
    }

    if (scheduleMode && mode === 'ride') {
      if (!scheduledDate || !scheduledTime) {
        alert('Please select both date and time for your scheduled ride');
        return;
      }

      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      const now = new Date();
      
      if (scheduledDateTime <= now) {
        alert('Scheduled time must be in the future');
        return;
      }

      const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      if (scheduledDateTime > sevenDaysLater) {
        alert('You can only schedule rides up to 7 days in advance');
        return;
      }
    }

    const user = localStorage.getItem('tekstar_user');
    if (!user) {
      navigate('/login', { state: { from: '/', pickup, dropoff, mode, scheduleMode, scheduledDate, scheduledTime } });
      return;
    }

    localStorage.setItem('ride_pickup', pickup);
    localStorage.setItem('ride_dropoff', dropoff);
    localStorage.setItem('ride_stops', JSON.stringify(stops));
    localStorage.setItem('booking_mode', mode);
    
    if (scheduleMode) {
      localStorage.setItem('ride_schedule_mode', 'true');
      localStorage.setItem('ride_scheduled_datetime', `${scheduledDate}T${scheduledTime}`);
    } else {
      localStorage.removeItem('ride_schedule_mode');
      localStorage.removeItem('ride_scheduled_datetime');
    }
    
    if (mode === 'delivery') {
      navigate('/book-delivery');
    } else {
      navigate('/select-ride');
    }
  };

  const filteredPickupLocations = mockLocations.filter(loc =>
    loc.toLowerCase().includes(pickup.toLowerCase())
  );

  const filteredDropoffLocations = mockLocations.filter(loc =>
    loc.toLowerCase().includes(dropoff.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Get a ride
                  <br />
                  <span className="text-pink-600">in minutes</span>
                </h1>
                <p className="text-xl text-gray-600">
                  Book a ride at any time and get to where you need to go.
                </p>
              </div>

              {/* Ride/Delivery Booking Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4 border border-gray-100">
                {/* Mode Selection Tabs */}
                <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
                  <button
                    onClick={() => { setMode('ride'); setScheduleMode(false); }}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                      mode === 'ride'
                        ? 'bg-pink-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Book a Ride
                    </div>
                  </button>
                  <button
                    onClick={() => { setMode('delivery'); setScheduleMode(false); }}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                      mode === 'delivery'
                        ? 'bg-pink-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Package className="h-4 w-4" />
                      Send Package
                    </div>
                  </button>
                </div>

                {/* Schedule Toggle */}
                {mode === 'ride' && (
                  <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
                    <button
                      onClick={() => setScheduleMode(false)}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        !scheduleMode
                          ? 'bg-pink-100 text-pink-700 shadow-sm'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Clock className="h-4 w-4" />
                        Ride now
                      </div>
                    </button>
                    <button
                      onClick={() => setScheduleMode(true)}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        scheduleMode
                          ? 'bg-pink-100 text-pink-700 shadow-sm'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Schedule for later
                      </div>
                    </button>
                  </div>
                )}

                {/* Schedule Form */}
                {scheduleMode && (
                  <ScheduleRideForm
                    scheduledDate={scheduledDate}
                    scheduledTime={scheduledTime}
                    onDateChange={setScheduledDate}
                    onTimeChange={setScheduledTime}
                  />
                )}

                <div className="space-y-4">
                  {/* Helper Text */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    💡 <strong>Tip:</strong> Use suggested locations for best results, or type specific Zambian addresses (e.g., "Kasama Town Centre, Kasama")
                  </div>

                  {/* Saved Places Quick Picks */}
                  {user && (
                    <SavedPlacesQuickPicks
                      onSelect={(addr) => (pickup ? setDropoff(addr) : setPickup(addr))}
                      currentValue={pickup || dropoff}
                    />
                  )}

                  {/* Pickup Location */}
                  <LocationInput
                    placeholder="Enter pickup location (e.g., Kasama Town Centre)"
                    value={pickup}
                    onChange={(e) => setPickup(e.target.value)}
                    onFocus={() => setShowPickupSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowPickupSuggestions(false), 200)}
                    suggestions={filteredPickupLocations}
                    showSuggestions={showPickupSuggestions}
                    onSelectSuggestion={(loc) => { setPickup(loc); setShowPickupSuggestions(false); }}
                    savedAddresses={savedAddresses}
                    user={user}
                    onSelectSavedAddress={setPickup}
                    markerColor="bg-gray-400"
                    showCurrentLocation={true}
                    onGetCurrentLocation={getCurrentLocation}
                    gettingLocation={gettingLocation}
                  />

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute left-[5px] top-0 bottom-0 w-[2px] bg-gray-200"></div>
                  </div>

                  {/* Dropoff Location */}
                  <LocationInput
                    placeholder="Enter destination"
                    value={dropoff}
                    onChange={(e) => setDropoff(e.target.value)}
                    onFocus={() => setShowDropoffSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowDropoffSuggestions(false), 200)}
                    suggestions={filteredDropoffLocations}
                    showSuggestions={showDropoffSuggestions}
                    onSelectSuggestion={(loc) => { setDropoff(loc); setShowDropoffSuggestions(false); }}
                    savedAddresses={savedAddresses}
                    user={user}
                    onSelectSavedAddress={setDropoff}
                    markerColor="bg-pink-600"
                  />

                  {/* Stops */}
                  {mode === 'ride' && (
                    <StopManager
                      stops={stops}
                      showStopSuggestions={showStopSuggestions}
                      setShowStopSuggestions={setShowStopSuggestions}
                      onAddStop={addStop}
                      onRemoveStop={removeStop}
                      onUpdateStop={updateStop}
                    />
                  )}

                  {/* Add Stop Button */}
                  {mode === 'ride' && stops.length < 2 && (
                    <button
                      onClick={addStop}
                      className="mt-4 ml-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add stop ({2 - stops.length} remaining)
                    </button>
                  )}
                </div>

                <Button
                  onClick={handleGetRide}
                  className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold rounded-xl transition-all"
                >
                  {mode === 'delivery' 
                    ? 'Continue to delivery details'
                    : scheduleMode 
                    ? 'Schedule ride' 
                    : 'Get ride estimates'
                  }
                </Button>
              </div>
            </div>

            {/* Right: Illustration */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-300 to-purple-300 rounded-3xl blur-3xl opacity-20"></div>
                <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                  <div className="aspect-square bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center">
                    <svg className="w-64 h-64" viewBox="0 0 200 200" fill="none">
                      <circle cx="100" cy="100" r="80" fill="#EC4899" opacity="0.1" />
                      <circle cx="100" cy="100" r="60" fill="#EC4899" opacity="0.2" />
                      <path d="M60 100 L90 120 L140 70" stroke="#EC4899" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <FeaturesSection />

      {/* Bus Booking Section */}
      {featureFlags.bus_booking_visible && <BusBookingPromo />}

      {/* Logistics Section */}
      {featureFlags.logistics_visible && <LogisticsPromo />}

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Home;
